// finance.repo — Beban/Pengeluaran CRUD (tabel expenses). Tiket dipakai dari reports.repo.
import { supabase } from '@core/supabase';
import { fetchAllPaged } from '@core/paging';
import { currentUser } from '@core/session';
import { genNoDok } from '@domain/format';

export interface Expense { id: number; no_dok?: string | null; is_aset?: boolean; akun_id?: number | null; tgl_iso: string; outlet: string | null; kategori: string | null; nama: string; jumlah: number; catatan: string | null; by_user: string | null; bukti: string | null }
// 'Ongkir' dipisah dari 'Transport' (Apo 29 Jul): ongkos kirim part antar cabang perlu terlihat
// sendiri supaya ketahuan berapa mahalnya mengambil dari cabang lain — kalau dicampur dengan
// transport harian, biaya itu tak pernah kelihatan.
export const BEBAN_KAT = ['Gaji', 'Komisi', 'Sewa', 'Listrik/Air', 'Internet', 'Peralatan', 'Transport', 'Ongkir', 'ATK', 'Marketing', 'Hadiah Loyalti', 'Konsumsi', 'Lainnya'];

// Kolom `no_dok` datang lewat migrasi 0076. Sampai Apo menjalankannya, kolomnya belum ada —
// dan menu Beban TIDAK boleh mati karena itu; nomor dokumen cukup ditampilkan "—".
const KOLOM_BEBAN = 'id,tgl_iso,outlet,kategori,nama,jumlah,catatan,by_user,bukti,is_aset,akun_id';
const kolomHilang = (msg: string): boolean => /no_dok|is_aset/i.test(msg) && /column|does not exist/i.test(msg);

/** Angka tiket untuk Laba-Rugi & Arus Kas — DIJUMLAHKAN DI DATABASE (migrasi 0088).
 *  Dulu semua tiket diunduh lalu dijumlahkan di HP: pada 300 tiket/hari itu salah (terpotong
 *  1.000 baris) sekaligus berat. Sekarang satu panggilan, satu baris hasil, seberapa pun datanya.
 *  Aturan uangnya tetap milik `@domain/money` — view `v_uang_tiket` adalah terjemahan setianya
 *  dan kesetaraannya diuji ke data nyata; kalau money.ts berubah, view itu WAJIB ikut. */
export interface FinAgg {
  omzet: number; hpp: number; kasMasuk: number; refund: number; biayaSimpan: number;
  tagihanBersih: number; unit: number; piutangBalance: number; dpTertahan: number;
}
export async function fetchFinAgg(from: string | null, to: string | null, outlet?: string): Promise<FinAgg> {
  const { data, error } = await supabase().rpc('finance_agg', { p_from: from, p_to: to, p_outlet: outlet ?? null });
  if (error) throw new Error(error.message);
  const r = ((data ?? []) as Record<string, string | number>[])[0];
  const n = (v: unknown): number => Number(v ?? 0);
  return {
    omzet: n(r?.['omzet']), hpp: n(r?.['hpp']), kasMasuk: n(r?.['kas_masuk']), refund: n(r?.['refund']),
    biayaSimpan: n(r?.['biaya_simpan']), tagihanBersih: n(r?.['tagihan_bersih']), unit: n(r?.['unit']),
    piutangBalance: n(r?.['piutang_saldo']), dpTertahan: n(r?.['dp_tertahan']),
  };
}

/** HPP SEMENTARA (part terbayar ke supplier, unit belum diambil) — migrasi 0103.
 *  `sementara` = beban HPP sementara periode; `priorPaid` = pembayaran periode-lalu utk unit yang
 *  diambil periode ini (pengurang HPP normal agar tak dobel). Basis-kas, auto-reklas ikut status. */
export async function fetchHppDefer(from: string | null, to: string | null, outlet?: string): Promise<{ sementara: number; priorPaid: number }> {
  const { data, error } = await supabase().rpc('hpp_defer_agg', { p_from: from, p_to: to, p_outlet: outlet ?? null });
  if (error) throw new Error(error.message);
  const r = ((data ?? []) as Record<string, string | number>[])[0];
  return { sementara: Number(r?.['sementara'] ?? 0), priorPaid: Number(r?.['prior_paid'] ?? 0) };
}

export interface HppRow { no: string; unit: string; modal: number }
/** Rincian tiket penyumbang HPP normal (modal sparepart, unit sudah diambil) — drill "klik HPP". */
export async function fetchHppRows(from: string | null, to: string | null, outlet?: string): Promise<HppRow[]> {
  const { data, error } = await supabase().rpc('hpp_rows', { p_from: from, p_to: to, p_outlet: outlet ?? null });
  if (error) throw new Error(error.message);
  return ((data ?? []) as Record<string, string | number>[]).map((r) => ({
    no: String(r['no'] ?? ''), unit: String(r['unit'] ?? '—'), modal: Number(r['modal'] ?? 0),
  }));
}

export interface HppDeferRow { no: string; unit: string; supplier: string; paid: number }
/** Rincian tiket penyumbang HPP Sementara (drill — No. Servis diklik → detail). */
export async function fetchHppDeferRows(from: string | null, to: string | null, outlet?: string): Promise<HppDeferRow[]> {
  const { data, error } = await supabase().rpc('hpp_defer_rows', { p_from: from, p_to: to, p_outlet: outlet ?? null });
  if (error) throw new Error(error.message);
  return ((data ?? []) as Record<string, string | number>[]).map((r) => ({
    no: String(r['no'] ?? ''), unit: String(r['unit'] ?? '—'), supplier: String(r['supplier'] ?? '—'), paid: Number(r['paid'] ?? 0),
  }));
}

/** Beban per kategori, dijumlahkan di database. `is_aset` dipisah karena belanja aset
 *  tidak mengurangi laba (keputusan Apo 21 Jul) — hanya penyusutannya yang jadi beban. */
export async function fetchBebanAgg(from: string | null, to: string | null, outlet?: string): Promise<{ beban: number; belanjaAset: number; bebanKat: [string, number][]; bebanBelumBayar: number }> {
  const { data, error } = await supabase().rpc('beban_agg', { p_from: from, p_to: to, p_outlet: outlet ?? null });
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as { kategori: string; jumlah: string | number; is_aset: boolean }[];
  let beban = 0, belanjaAset = 0;
  const kat = new Map<string, number>();
  for (const r of rows) {
    const j = Number(r.jumlah ?? 0);
    if (r.is_aset) { belanjaAset += j; continue; }
    beban += j;
    kat.set(r.kategori, (kat.get(r.kategori) ?? 0) + j);
  }
  // Urut mengikuti BEBAN_KAT supaya tampilannya tetap sama seperti sebelumnya
  const bebanKat = BEBAN_KAT.map((k) => [k, kat.get(k) ?? 0] as [string, number]).filter(([, v]) => v > 0);
  for (const [k, v] of kat) if (!BEBAN_KAT.includes(k) && v > 0) bebanKat.push([k, v]);
  // Beban BELUM DIBAYAR (akun_id null = hutang usaha) — TAK boleh mengurangi kas (Arus Kas/Posisi),
  // hanya jadi kewajiban. Non-aset. Paged (akun_id null biasanya sedikit, tapi jaring pengaman 1.000).
  const bebanBelumBayar = (await fetchAllPaged<{ jumlah: string | number }>((a, b) => {
    let q = supabase().from('expenses').select('jumlah').is('akun_id', null).not('is_aset', 'is', true);
    if (from) q = q.gte('tgl_iso', from);
    if (to) q = q.lte('tgl_iso', to);
    if (outlet) q = q.eq('outlet', outlet);
    return q.order('id').range(a, b);
  })).reduce((s, r) => s + Number(r.jumlah ?? 0), 0);
  return { beban, belanjaAset, bebanKat, bebanBelumBayar };
}

/** Kas masuk dikelompokkan per cara bayar × cabang (RPC 0088) — bahan rincian "Kas masuk per akun". */
export async function fetchKasPerCara(from: string | null, to: string | null, outlet?: string): Promise<{ cara: string | null; outlet: string | null; jumlah: number }[]> {
  const { data, error } = await supabase().rpc('kas_per_cara', { p_from: from, p_to: to, p_outlet: outlet ?? null });
  if (error) throw new Error(error.message);
  return ((data ?? []) as { cara: string | null; outlet: string | null; jumlah: string | number }[])
    .map((r) => ({ cara: r.cara ?? null, outlet: r.outlet ?? null, jumlah: Number(r.jumlah ?? 0) }));
}

export async function listExpenses(): Promise<Expense[]> {
  // Kolom `no_dok` belum tentu ada di basis lama → coba dulu, mundur ke set kolom minimal.
  const q = () => supabase().from('expenses');
  try {
    return await fetchAllPaged<Expense>((a, b) => q().select(`${KOLOM_BEBAN},no_dok`)
      .order('tgl_iso', { ascending: false }).order('id').range(a, b)) as unknown as Expense[];
  } catch (e) {
    if (!kolomHilang((e as Error).message)) throw e;
    return await fetchAllPaged<Expense>((a, b) => q().select(KOLOM_BEBAN)
      .order('tgl_iso', { ascending: false }).order('id').range(a, b)) as unknown as Expense[];
  }
}
/** Satu beban (untuk popup rincian dari menu Penyimpanan). */
export async function getExpense(id: number): Promise<Expense> {
  const q = () => supabase().from('expenses');
  const r1 = await q().select(`${KOLOM_BEBAN},no_dok`).eq('id', id).single();
  const r2 = r1.error && kolomHilang(r1.error.message) ? await q().select(KOLOM_BEBAN).eq('id', id).single() : r1;
  if (r2.error) throw new Error(r2.error.message);
  return r2.data as unknown as Expense;
}

export async function addExpense(d: { tgl_iso: string; outlet: string | null; kategori: string; nama: string; jumlah: number; catatan: string; bukti?: string | null; is_aset?: boolean; akun_id?: number | null }): Promise<string> {
  // Nomor dokumen dibuat di sini, bukan di layar: setiap catatan beban HARUS punya nomor yang
  // bisa ditulis di kuitansi & dirujuk dari bukti di menu Penyimpanan. Kalau bentrok (praktis
  // mustahil, acak 32^4), ambil nomor lain — jangan tumpahkan error basis data ke petugas.
  const nama = currentUser()?.nama ?? '';
  for (let coba = 0; ; coba++) {
    const no_dok = genNoDok('BBN');
    const { error } = await supabase().from('expenses').insert({ ...d, no_dok, by_user: nama });
    if (!error) return no_dok;
    const bentrok = error.code === '23505' || /duplicate key|uq_expenses_no_dok/i.test(error.message);
    // Kolom belum ada (migrasi 0076 belum di-push) → simpan tanpa nomor, jangan menghalangi kasir.
    if (/no_dok/i.test(error.message) && /column/i.test(error.message)) {
      const { error: e2 } = await supabase().from('expenses').insert({ ...d, by_user: nama });
      if (e2) throw new Error(e2.message);
      return '';
    }
    if (!bentrok || coba >= 2) throw new Error(error.message);
  }
}

// ---- Bukti beban (Supabase Storage, bucket privat 'bukti'; path <outlet>/<uuid>.<ext>) ----
/** Upload bukti → return path. contentType di-SET aman oleh pemanggil (bukan file.type mentah — cegah SVG/HTML aktif).
 *  RLS storage (0053) batasi non-mgr Finance ke folder cabangnya. */
export async function uploadBukti(folder: string, file: Blob, ext: string, contentType: string): Promise<string> {
  const path = `${folder}/${crypto.randomUUID()}.${ext}`;
  const { error } = await supabase().storage.from('bukti').upload(path, file, { contentType, upsert: false });
  if (error) throw new Error(error.message);
  return path;
}
/** Signed URL (120 dtk — user langsung buka) utk lihat bukti privat. */
export async function buktiSignedUrl(path: string): Promise<string> {
  const { data, error } = await supabase().storage.from('bukti').createSignedUrl(path, 120);
  if (error) throw new Error(error.message);
  return data.signedUrl;
}
/** Hapus objek bukti (best-effort) — anti-orphan saat hapus/replace beban. */
export async function deleteBukti(path: string): Promise<void> {
  await supabase().storage.from('bukti').remove([path]);
}
export async function updateExpense(id: number, d: Record<string, unknown>): Promise<void> {
  const { error } = await supabase().from('expenses').update(d).eq('id', id); if (error) throw new Error(error.message);
}
export async function removeExpense(id: number): Promise<void> {
  const { data } = await supabase().from('expenses').select('bukti').eq('id', id).maybeSingle(); // ambil path bukti dulu (anti-orphan)
  const { error } = await supabase().from('expenses').delete().eq('id', id); if (error) throw new Error(error.message);
  const path = (data as { bukti: string | null } | null)?.bukti;
  if (path) { try { await deleteBukti(path); } catch { /* best-effort */ } } // hapus objek storage
}
